import React, { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Collapse, Toolbar } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SearchContext } from '../Context/SearchContext/SearchContext';
import { LoggingContext } from '../Context/LoggingContext/LoggingContext';
import FilterCheckboxes from './FilterCheckboxes';
import { GlobalContext } from '../Context/GlobalContext/GlobalContextProvider';
import SizeSlider from './SizeSlider/SizeSlider';
import { FilterTypeLabelsCheckbox } from '../types/reducers/filterReducer';
import { TeeMeasureLabel } from '../types/databaseTables/idealteemeasures';
import FitAndReliabilityFilterSlider from './FitAndReliabilityFilterSlider';

const measureLabels: TeeMeasureLabel[] = [
  'armholeopening',
  'chest',
  'frontlength',
  'hem',
  'neckwidth',
  'shouldertoshoulder',
  'sleevelength',
  'sleeveopening',
  'necktoshoulder',
  'waist',
];

function UpdateResultsButton() {
  const { updateResults, filterState } = useContext(SearchContext);
  const [resultsUpToDate, setResultsUpToDate] = useState(true);
  // Update state to determine whether Update Results button is disabled
  React.useEffect(() => {
    const { externalFilteredGarmentObjects, internalFilteredGarmentObjects } = filterState;
    // We want to setResultsUpToDate(false) if there has been a change to the internalFilteredGarmentObjects that hasn't been updated in the externalFilteredGarmentObjects
    if (resultsUpToDate) {
      if (
        // First, check if externalFilteredGarmentObjects is the least recent. If so, the results may not be up to date.
        filterState.timestamps.externalFilteredGarmentObjects <
          filterState.timestamps.internalFilteredGarmentObjects &&
        // Second, check the post lengths are the different. If they are, the results are not up to date
        (externalFilteredGarmentObjects.length !== internalFilteredGarmentObjects.length ||
          // Third, iterate through the arrays to check if they are the different. If they are different, the results are not up to date and we want to setResultsUpToDate to false
          externalFilteredGarmentObjects.filter(
            (thisExtObj, index) =>
              thisExtObj.teemeasuresaverages_id !==
              internalFilteredGarmentObjects[index].teemeasuresaverages_id
          ).length !== 0)
      ) {
        setResultsUpToDate(false);
      }
      // else if resultsUpToDate === false, then we need to check if we want to setResultsUpToDate(true); ie, are the results now up to date?
    } else if (
      // The results are up to date if the lengths are the same and the filters match
      externalFilteredGarmentObjects.length === internalFilteredGarmentObjects.length &&
      // Third, iterate through the arrays to check if they are the different. If they are different, the results are not up to date and we want to setResultsUpToDate to false
      externalFilteredGarmentObjects.filter(
        (thisExtObj, index) =>
          thisExtObj.teemeasuresaverages_id !==
          internalFilteredGarmentObjects[index].teemeasuresaverages_id
      ).length === 0
    ) {
      setResultsUpToDate(true);
    }
  }, [
    filterState.timestamps.externalFilteredGarmentObjects,
    filterState.timestamps.internalFilteredGarmentObjects,
  ]);

  return (
    <Button
      sx={{ position: 'fixed', zIndex: 900 }}
      color={resultsUpToDate ? 'success' : 'secondary'}
      disabled={resultsUpToDate}
      onClick={updateResults}
      variant='contained'
    >
      {`${resultsUpToDate ? 'Results Up To Date' : `Update Results`} ${
        Object.keys(filterState.internalFilteredGarmentObjects).length
      }/${Object.keys(filterState.initialArrayOfPosts).length}`}
    </Button>
  );
}

type DropdownLabel = FilterTypeLabelsCheckbox | 'measureSliders' | 'fitAndReliabilitySliders';

function Filter() {
  const location = useLocation();
  const { filterState, dispatchFilter } = useContext(SearchContext);
  const { filterMobileOpen } = React.useContext(GlobalContext);
  const { addLog } = useContext(LoggingContext);
  const [dropDownOpen, setDropDownOpen] = useState<Record<DropdownLabel, boolean>>({
    manufacturer: false,
    description: false,
    availablecolors: false,
    measureSliders: false,
    fitAndReliabilitySliders: false,
  });

  // I want filterMobileOpen in state so that filter still renders when I slide window width back and forth
  const doNothing = () => 'nothing';
  if (filterMobileOpen) {
    doNothing();
  }

  function clickDropdownButton(label: DropdownLabel) {
    addLog(`Clicked filter dropdown button for: ${label}`, location.pathname);
    setDropDownOpen((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  return (
    <Box>
      <Toolbar />
      {/* <br /> */}
      <Box sx={{ justifyContent: 'center', display: 'flex', marginTop: '4px' }}>
        <UpdateResultsButton />
      </Box>
      <br />
      <br />
      <Button // Size Sliders
        sx={{ width: '100%' }}
        endIcon={<ArrowDropDownIcon />}
        onClick={() => clickDropdownButton('measureSliders')}
      >
        Measurements
      </Button>
      <Collapse in={dropDownOpen.measureSliders}>
        {measureLabels.map((thisLabel) => (
          <SizeSlider
            key={thisLabel}
            filterLabel={thisLabel}
            filterObject={filterState.filterSliderObjects[thisLabel as TeeMeasureLabel]}
            dispatchFilter={dispatchFilter}
            dropdownOpen={dropDownOpen.measureSliders}
          />
        ))}
      </Collapse>
      <Button // Fit and reliability
        sx={{ width: '100%' }}
        endIcon={<ArrowDropDownIcon />}
        onClick={() => clickDropdownButton('fitAndReliabilitySliders')}
      >
        Fit &#38; Reliability
      </Button>
      <Collapse in={dropDownOpen.fitAndReliabilitySliders}>
        <FitAndReliabilityFilterSlider
          filterLabel='fit'
          filterObject={filterState.fit}
          dispatchFilter={dispatchFilter}
        />
        <FitAndReliabilityFilterSlider
          filterLabel='reliability'
          filterObject={filterState.reliability}
          dispatchFilter={dispatchFilter}
        />
      </Collapse>
      {['manufacturer', 'description', 'availablecolors'].map((thisLabel) => (
        <div key={thisLabel}>
          <Button
            sx={{ width: '100%', textDecoration: 'capitalize' }}
            endIcon={<ArrowDropDownIcon />}
            onClick={() => clickDropdownButton(thisLabel as FilterTypeLabelsCheckbox)}
          >
            {thisLabel === 'availablecolors' ? 'available colors' : thisLabel}
          </Button>
          <Collapse in={dropDownOpen[thisLabel as FilterTypeLabelsCheckbox]}>
            <FilterCheckboxes
              filterLabel={thisLabel as FilterTypeLabelsCheckbox}
              filterObject={
                filterState.filterCheckboxObjects[thisLabel as FilterTypeLabelsCheckbox]
              }
              dispatchFilter={dispatchFilter}
            />
          </Collapse>
        </div>
      ))}
      <br />
      <Box sx={{ justifyContent: 'center', display: 'flex', marginTop: '4px' }}>
        <Button
          onClick={()=> dispatchFilter({
            type: "clear all filters"
          })}
          variant='outlined'
        >
          Clear All Filters
        </Button>
      </Box>
    </Box>
  );
}

export default Filter;
